// The Meter's arithmetic (docs/13 "Free tools"): a range of tokens for a prompt and a range of
// cost at the rates the reader enters. Token counts are estimated from characters, because every
// provider's tokenizer differs and none publishes a meter: the honest answer is a range.

/** English text runs about 4 characters a token; code and other languages run denser. In tenths of
 *  a character, so the division is exact. */
const TENTHS_PER_TOKEN = { low: 46, high: 32 } as const;

export interface Range {
  low: number;
  high: number;
}

export interface MeterInput {
  prompt: string;
  /** Tokens already in the conversation, re-sent with this prompt. */
  context: number;
  /** The answer's expected length in tokens. */
  output: Range;
  /** Hidden reasoning tokens as a multiple of the answer (0 when off). */
  thinking: Range;
  /** Dollars per million tokens. */
  inputRate: number;
  outputRate: number;
}

export interface MeterEstimate {
  inputTokens: Range;
  outputTokens: Range;
  cost: Range;
}

export function promptTokens(text: string): Range {
  const chars = text.trim().length;
  if (chars === 0) return { low: 0, high: 0 };
  return { low: Math.ceil((chars * 10) / TENTHS_PER_TOKEN.low), high: Math.ceil((chars * 10) / TENTHS_PER_TOKEN.high) };
}

export function estimate(input: MeterInput): MeterEstimate {
  const p = promptTokens(input.prompt);
  const inputTokens = { low: p.low + input.context, high: p.high + input.context };
  const outputTokens = {
    low: Math.round(input.output.low * (1 + input.thinking.low)),
    high: Math.round(input.output.high * (1 + input.thinking.high)),
  };
  const price = (tin: number, tout: number) => (tin * input.inputRate + tout * input.outputRate) / 1_000_000;
  return { inputTokens, outputTokens, cost: { low: price(inputTokens.low, outputTokens.low), high: price(inputTokens.high, outputTokens.high) } };
}

/** "$0.0042", "$1.20", "$1,240": enough digits to see a small cost, no more. */
export function dollars(n: number): string {
  if (n === 0) return "$0";
  if (n < 0.01) return `$${n.toPrecision(2)}`;
  return `$${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
